import { useState, useEffect, useCallback } from 'react';
import { DataTable } from '../../components/shared/DataTable';
import { CourseGroupModal } from '../../components/content/CourseGroupModal';
import { ConfirmDeleteModal } from '../../components/content/ConfirmDeleteModal';
import { searchCourseGroups, deleteCourseGroup } from '../../services/content/courseGroupService';
import { useRefresh } from '../../contexts/RefreshContext';
import type { CourseGroup } from '../../types/content/courseGroup';
import type { Column } from '../../components/shared/DataTable';

function RowMenu({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100 transition-colors">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[100px]">
          <button onClick={() => { setOpen(false); onEdit(); }} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Edit</button>
          <button onClick={() => { setOpen(false); onDelete(); }} className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50">Delete</button>
        </div>
      )}
    </div>
  );
}

const columns: Column<CourseGroup>[] = [
  { key: 'name', label: 'Name' },
  {
    key: 'courseIds',
    label: 'Courses',
    render: (item) => `${item.courseIds?.length ?? 0} course(s)`,
  },
  {
    key: 'courseCategory',
    label: 'Categories',
    render: (item) => item.courseCategory?.join(', ') || '—',
  },
];

export default function CourseGroupsTab() {
  const [data, setData] = useState<CourseGroup[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalItem, setModalItem] = useState<CourseGroup | undefined>(undefined);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const results = await searchCourseGroups({}, { page, pageSize, orderBy: 'updated', order: 'desc' });
      setData(results);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => { fetch(); }, [fetch]);

  const { registerRefreshHandler, unregisterRefreshHandler } = useRefresh();
  useEffect(() => {
    registerRefreshHandler('/content/course-groups', fetch);
    return () => unregisterRefreshHandler('/content/course-groups');
  }, [fetch, registerRefreshHandler, unregisterRefreshHandler]);

  async function handleDelete(id: string) {
    await deleteCourseGroup(id);
    setPendingDeleteId(null);
    fetch();
  }

  return (
    <>
      <DataTable
        title="Course Groups"
        columns={columns}
        data={data}
        page={page}
        pageSize={pageSize}
        loading={loading}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
        onRefresh={fetch}
        onAdd={() => { setModalItem(undefined); setShowModal(true); }}
        alignTop
        rowMenu={(item) => (
          <RowMenu
            onEdit={() => { setModalItem(item); setShowModal(true); }}
            onDelete={() => setPendingDeleteId(item.id)}
          />
        )}
      />
      {pendingDeleteId && (
        <ConfirmDeleteModal title="Delete Course Group" message="Are you sure you want to delete this course group?" onCancel={() => setPendingDeleteId(null)} onConfirm={() => handleDelete(pendingDeleteId)} />
      )}
      {showModal && (
        <CourseGroupModal
          courseGroup={modalItem}
          onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); fetch(); }}
        />
      )}
    </>
  );
}
